import { Button } from '@bennows/boows-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiFailure, call } from '../../lib/api';
import { passkeyErrorKind, getPasskeyCredential } from '../../lib/webauthn';
import { codeFailureKey } from './useCodeStep';
import { ME_QUERY_KEY } from './useOperatorMe';

/** The message key for a failed passkey step, or `null`. */
function passkeyFailureKey(error: unknown): string | null {
  if (!error) return null;
  if (!(error instanceof ApiFailure)) return passkeyErrorKind(error) === 'cancelled' ? 'signIn.passkeyCancelled' : 'signIn.passkeyFailed';
  if (error.slug === 'second-factor-invalid') return 'signIn.passkeyInvalid';
  if (error.slug === 'passkey-challenge-expired' || error.slug === 'passkey-not-set-up') return 'signIn.passkeyFailed';
  return codeFailureKey(error);
}

/**
 * Step 2 of the sign-in with a passkey, offered first when the operator has one
 * (REQ-140-004, T460-1-4): challenge, browser dialog, signed answer. The
 * authenticator code stays one click away.
 */
export function PasskeyStep({ onVerified, onUseCode, onStartOver }: { readonly onVerified: () => void; readonly onUseCode: () => void; readonly onStartOver: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const messageRef = useRef<HTMLParagraphElement>(null);
  const signIn = useMutation({
    mutationFn: async () => {
      const { options } = await call(() => api.POST('/v1/operator/sessions/current/passkey-options'));
      const response = await getPasskeyCredential(options);
      await call(() => api.POST('/v1/operator/sessions/current/passkey', { body: { response: response as unknown as Record<string, unknown> } }));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      onVerified();
    },
  });
  const failure = signIn.isPending ? null : passkeyFailureKey(signIn.error);

  useEffect(() => {
    if (failure) messageRef.current?.focus();
  }, [failure]);

  return (
    <>
      <h1>{t('signIn.passkeyTitle')}</h1>
      <p className="ui-auth__lead">{t('signIn.passkeyLead')}</p>
      {failure ? (
        <p className="ui-auth__message" role="alert" tabIndex={-1} ref={messageRef}>
          {t(failure)}
        </p>
      ) : null}
      <Button iconPrefix={<KeyRound aria-hidden="true" />} loading={signIn.isPending} onClick={() => signIn.mutate()} block autoFocus>
        {t('signIn.passkeySubmit')}
      </Button>
      <Button variant="secondary" onClick={onUseCode} block>
        {t('signIn.useCode')}
      </Button>
      <Button variant="ghost" onClick={onStartOver}>
        {t('signIn.startOver')}
      </Button>
    </>
  );
}
